import { Vec3, add, scale, normalize, length } from '../core/math/vec3'
import { unrotate } from '../core/math/quat'
import { clamp } from '../core/math/scalar'
import { densityAt } from '../core/world/atmosphere'
import { altitudeOf } from '../core/dynamics/flight'
import { GimbalCommand } from '../core/propulsion/engine'
import { localUp, localNorth, localEast } from '../core/nav/attitude'
import { surfaceVelocity } from '../core/nav/readouts'
import { Simulation, SimVessel } from './simulation'

export type AutopilotMode = 'spento' | 'assetto' | 'prograde' | 'retrograde' | 'radiale' | 'bersaglio'

export interface Autopilot {
  mode: AutopilotMode
  /** beccheggio richiesto sopra l'orizzonte, rad */
  pitchTarget: number
  /** rotta richiesta sulla bussola locale, rad */
  headingTarget: number
  /** direzione mondo da inseguire in modalita 'bersaglio', es. un nodo di manovra */
  bersaglio: Vec3 | null
}

export const autopilotIniziale = (): Autopilot => ({
  mode: 'spento',
  pitchTarget: Math.PI / 2,
  headingTarget: Math.PI / 2,
  bersaglio: null,
})

/** Direzione verso cui il muso dovrebbe puntare, in coordinate mondo. */
export function desiredDirection(sim: Simulation, sv: SimVessel, ap: Autopilot): Vec3 | null {
  const p = sv.state.position
  switch (ap.mode) {
    case 'spento':
      return null
    case 'assetto': {
      const up = localUp(p)
      const north = localNorth(p)
      const east = localEast(p)
      const orizzontale = add(
        scale(north, Math.cos(ap.headingTarget)),
        scale(east, Math.sin(ap.headingTarget)),
      )
      return normalize(add(
        scale(up, Math.sin(ap.pitchTarget)),
        scale(orizzontale, Math.cos(ap.pitchTarget)),
      ))
    }
    case 'prograde':
    case 'retrograde': {
      // in atmosfera conta la velocita rispetto all'aria, fuori quella orbitale
      const inAria = densityAt(sim.body.atmosphere, altitudeOf(p, sim.body)) > 0
      const v = inAria ? surfaceVelocity(sv.state, sim.body) : sv.state.velocity
      if (length(v) < 1) return null
      return ap.mode === 'prograde' ? normalize(v) : scale(normalize(v), -1)
    }
    case 'radiale':
      return localUp(p)
    case 'bersaglio':
      return ap.bersaglio ? normalize(ap.bersaglio) : null
  }
}

export interface GimbalGains {
  readonly kp: number
  readonly kd: number
}

export const GUADAGNI: GimbalGains = { kp: 7.5, kd: 21 }

/**
 * Comando di gimbal per portare il muso su una direzione.
 *
 * Come sono orientati i segni, che e la parte in cui e facile sbagliare:
 * con l'ugello sotto il baricentro, un comando di beccheggio positivo genera
 * una coppia che porta il muso verso +X del corpo, e un comando di imbardata
 * positivo lo porta verso −Z. Il termine derivativo non deriva l'errore
 * numericamente ma legge la velocita angolare, che e gia nello stato: piu
 * pulito e senza il rumore della differenza fra due passi.
 */
export function autopilotGimbal(
  sv: SimVessel, desired: Vec3 | null, gains: GimbalGains = GUADAGNI,
): GimbalCommand {
  if (!desired) return { pitch: 0, yaw: 0 }

  const bersaglio = unrotate(sv.state.orientation, desired)
  const w = sv.state.angularVelocity

  // se il bersaglio e alle spalle, l'errore laterale e piccolo ma la manovra
  // e enorme: si satura il comando invece di rispondere piano
  if (bersaglio.y < 0) {
    const s = Math.hypot(bersaglio.x, bersaglio.z) || 1
    return { pitch: clamp((bersaglio.x / s) * 1e3, -1, 1), yaw: clamp((-bersaglio.z / s) * 1e3, -1, 1) }
  }

  return {
    pitch: clamp(gains.kp * bersaglio.x + gains.kd * w.z, -1, 1),
    yaw: clamp(-gains.kp * bersaglio.z + gains.kd * w.x, -1, 1),
  }
}

/** Errore d'assetto residuo, in radianti. Serve alla UI e ai test. */
export function attitudeError(sv: SimVessel, desired: Vec3 | null): number {
  if (!desired) return 0
  const bersaglio = unrotate(sv.state.orientation, desired)
  return Math.atan2(Math.hypot(bersaglio.x, bersaglio.z), bersaglio.y)
}
